import { useEffect, useState, useCallback, useRef } from 'react';
import {
  X, Clock, CheckSquare, MessageCircle, FileText, Phone, Calendar, Send,
  Trash2, Plus, Check, AlertCircle, Mail, Users
} from 'lucide-react';
import { crmApi } from './crmApi';

export type VinculoCarteira = {
  leadId?: string;
  contatoId?: string;
  empresaContaId?: string;
  negocioId?: string;
};

interface Props {
  session: any;
  open: boolean;
  onClose: () => void;
  vinculo: VinculoCarteira;
  titulo: string;            // ex.: nome do contato
  subtitulo?: string;         // ex.: empresa / cargo
  telefone?: string;          // para WhatsApp 1-a-1
  responsavelId?: string | null;
  initialAba?: AbaId;
}

type AbaId = 'timeline' | 'tarefas' | 'whatsapp';

const TIPO_ICON: Record<string, any> = {
  nota: FileText,
  ligacao: Phone,
  email: Mail,
  whatsapp: MessageCircle,
  tarefa: CheckSquare,
  reuniao: Users,
};

const TIPO_COR: Record<string, string> = {
  nota: 'bg-amber-100 text-amber-700',
  ligacao: 'bg-blue-100 text-blue-700',
  email: 'bg-purple-100 text-purple-700',
  whatsapp: 'bg-green-100 text-green-700',
  tarefa: 'bg-orange-100 text-orange-700',
  reuniao: 'bg-indigo-100 text-indigo-700',
};

const TIPO_LABEL: Record<string, string> = {
  nota: 'Nota',
  ligacao: 'Ligação',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  tarefa: 'Tarefa',
  reuniao: 'Reunião',
};

const fmtDateTime = (d: any) => {
  if (!d) return '';
  try {
    const dt = new Date(d.$date || d);
    return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return ''; }
};

const CardDrawer = ({ session, open, onClose, vinculo, titulo, subtitulo, telefone, responsavelId, initialAba }: Props) => {
  const [aba, setAba] = useState<AbaId>(initialAba || 'timeline');

  // Sincroniza aba quando o drawer abre com initialAba diferente
  useEffect(() => {
    if (open && initialAba) setAba(initialAba);
  }, [open, initialAba]);
  const [atividades, setAtividades] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(false);
  const inputDescRef = useRef<HTMLTextAreaElement | null>(null);

  // Form criar atividade
  const [novoTipo, setNovoTipo] = useState<'nota' | 'ligacao' | 'email' | 'reuniao'>('nota');
  const [novoTexto, setNovoTexto] = useState('');

  // Form criar tarefa
  const [tarefaTitulo, setTarefaTitulo] = useState('');
  const [tarefaData, setTarefaData] = useState('');

  // WhatsApp
  const [waMensagem, setWaMensagem] = useState('');
  const [waEnviando, setWaEnviando] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateId, setTemplateId] = useState('');

  const carregar = useCallback(async () => {
    if (!open) return;
    setCarregando(true);
    try {
      const list = await crmApi.atividades.listar(session, vinculo);
      setAtividades(list);
    } catch (e: any) { console.error(e); }
    finally { setCarregando(false); }
  }, [open, vinculo, session]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (open && aba === 'whatsapp' && templates.length === 0) {
      crmApi.templates.listar(session).then(setTemplates).catch(() => {});
    }
  }, [open, aba, session, templates.length]);

  const adicionarAtividade = async () => {
    if (!novoTexto.trim()) return;
    try {
      await crmApi.atividades.criar(session, {
        tipo: novoTipo, descricao: novoTexto, ...vinculo, responsavelId,
      });
      setNovoTexto('');
      carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
  };

  const adicionarTarefa = async () => {
    if (!tarefaTitulo.trim()) return;
    try {
      await crmApi.atividades.criar(session, {
        tipo: 'tarefa',
        titulo: tarefaTitulo,
        descricao: tarefaTitulo,
        dataLembrete: tarefaData || null,
        concluida: false,
        ...vinculo, responsavelId,
      });
      setTarefaTitulo('');
      setTarefaData('');
      carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
  };

  const toggleTarefa = async (a: any) => {
    try {
      await crmApi.atividades.atualizar(session, a.id, { concluida: !a.concluida });
      carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
  };

  const removerAtividade = async (a: any) => {
    if (!confirm('Remover este registro?')) return;
    try { await crmApi.atividades.deletar(session, a.id); carregar(); }
    catch (e: any) { alert('Erro: ' + e.message); }
  };

  const enviarWhatsapp = async () => {
    if (!waMensagem.trim() && !templateId) return;
    if (!telefone) { alert('Sem telefone cadastrado neste registro.'); return; }
    setWaEnviando(true);
    try {
      const r = await crmApi.whatsapp.enviar(session, {
        numero: telefone, mensagem: waMensagem || undefined, templateId: templateId || undefined,
        ...vinculo,
      });
      if (r.success) {
        setWaMensagem(''); setTemplateId('');
        carregar();
      } else {
        alert('Falha ao enviar: ' + JSON.stringify(r.info));
      }
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setWaEnviando(false); }
  };

  const tarefas = atividades.filter(a => a.tipo === 'tarefa');
  const tarefasPendentes = tarefas.filter(t => !t.concluida);
  const tarefasFeitas = tarefas.filter(t => t.concluida);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="ml-auto h-full w-full max-w-xl bg-white shadow-2xl flex flex-col relative">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">{titulo}</h2>
            {subtitulo && <p className="text-xs text-gray-500 truncate">{subtitulo}</p>}
            {telefone && <p className="text-xs text-gray-400 mt-0.5">📱 {telefone}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 border-b border-gray-200 flex gap-1">
          {([
            { id: 'timeline', label: 'Timeline', Icon: Clock },
            { id: 'tarefas', label: `Tarefas${tarefasPendentes.length ? ` (${tarefasPendentes.length})` : ''}`, Icon: CheckSquare },
            { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
          ] as { id: AbaId; label: string; Icon: any }[]).map(t => (
            <button key={t.id} onClick={() => setAba(t.id)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
                aba === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}>
              <t.Icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* TIMELINE */}
          {aba === 'timeline' && (
            <div className="p-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <div className="flex flex-wrap gap-1 mb-2">
                  {(['nota', 'ligacao', 'email', 'reuniao'] as const).map(t => {
                    const Ic = TIPO_ICON[t];
                    return (
                      <button key={t} onClick={() => { setNovoTipo(t); inputDescRef.current?.focus(); }}
                        className={`px-2.5 py-1 text-xs rounded-md border flex items-center gap-1 ${
                          novoTipo === t ? 'bg-white border-gray-400 shadow-sm' : 'bg-transparent border-gray-200 text-gray-600'
                        }`}>
                        <Ic className="h-3 w-3" /> {TIPO_LABEL[t]}
                      </button>
                    );
                  })}
                </div>
                <textarea ref={inputDescRef} value={novoTexto} onChange={e => setNovoTexto(e.target.value)}
                  rows={2} placeholder={`Registrar ${TIPO_LABEL[novoTipo].toLowerCase()}...`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md resize-none" />
                <div className="flex justify-end mt-2">
                  <button onClick={adicionarAtividade} disabled={!novoTexto.trim()}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-md hover:bg-gray-700 disabled:opacity-40 flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Registrar
                  </button>
                </div>
              </div>

              {carregando ? (
                <p className="text-xs text-gray-500 text-center py-8">Carregando…</p>
              ) : atividades.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8">Nenhuma atividade ainda. Comece registrando uma nota acima.</p>
              ) : (
                <ol className="space-y-3">
                  {atividades.map(a => {
                    const Icon = TIPO_ICON[a.tipo] || FileText;
                    return (
                      <li key={a.id} className="flex gap-3 group">
                        <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${TIPO_COR[a.tipo] || 'bg-gray-100 text-gray-700'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                              {TIPO_LABEL[a.tipo] || a.tipo}
                              {a.tipo === 'tarefa' && a.concluida && <span className="ml-2 text-green-600 font-normal">✓ concluída</span>}
                            </span>
                            <button onClick={() => removerAtividade(a)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {a.titulo && <p className="text-sm font-medium text-gray-900 mt-0.5">{a.titulo}</p>}
                          <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{a.descricao}</p>
                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-400">
                            <Clock className="h-3 w-3" /> {fmtDateTime(a.data)}
                            {a.responsavelId && <span>· por {a.responsavelId}</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {/* TAREFAS */}
          {aba === 'tarefas' && (
            <div className="p-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <input type="text" value={tarefaTitulo} onChange={e => setTarefaTitulo(e.target.value)}
                  placeholder="O que precisa ser feito?"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md mb-2" />
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <input type="datetime-local" value={tarefaData} onChange={e => setTarefaData(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md" />
                  <button onClick={adicionarTarefa} disabled={!tarefaTitulo.trim()}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-md hover:bg-gray-700 disabled:opacity-40">
                    Adicionar
                  </button>
                </div>
              </div>

              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Pendentes ({tarefasPendentes.length})</h3>
              <div className="space-y-1.5 mb-4">
                {tarefasPendentes.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Nenhuma tarefa pendente.</p>
                ) : tarefasPendentes.map(t => {
                  const dl = t.dataLembrete ? new Date(t.dataLembrete.$date || t.dataLembrete) : null;
                  const atrasada = dl && dl < new Date();
                  return (
                    <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 group">
                      <button onClick={() => toggleTarefa(t)}
                        className="h-4 w-4 border-2 border-gray-300 rounded hover:border-gray-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{t.titulo || t.descricao}</p>
                        {dl && <p className={`text-[11px] flex items-center gap-1 ${atrasada ? 'text-red-600' : 'text-gray-500'}`}>
                          {atrasada && <AlertCircle className="h-3 w-3" />} {fmtDateTime(dl)}
                        </p>}
                      </div>
                      <button onClick={() => removerAtividade(t)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {tarefasFeitas.length > 0 && (
                <>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Concluídas</h3>
                  <div className="space-y-1.5">
                    {tarefasFeitas.map(t => (
                      <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-md group">
                        <button onClick={() => toggleTarefa(t)}
                          className="h-4 w-4 bg-green-600 border-2 border-green-600 rounded flex items-center justify-center flex-shrink-0">
                          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                        </button>
                        <p className="flex-1 min-w-0 text-sm text-gray-500 line-through truncate">{t.titulo || t.descricao}</p>
                        <button onClick={() => removerAtividade(t)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* WHATSAPP */}
          {aba === 'whatsapp' && (
            <div className="p-4">
              {!telefone && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Sem telefone cadastrado. Edite o registro para adicionar e poder enviar WhatsApp.</span>
                </div>
              )}

              <div className="mb-3">
                <label className="text-xs text-gray-600">Template (opcional)</label>
                <select value={templateId} onChange={e => setTemplateId(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white">
                  <option value="">— Sem template —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>

              <div className="mb-3">
                <label className="text-xs text-gray-600">Mensagem</label>
                <textarea value={waMensagem} onChange={e => setWaMensagem(e.target.value)}
                  rows={5} placeholder="Digite a mensagem ou selecione um template…"
                  className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md resize-none" />
              </div>

              <button onClick={enviarWhatsapp} disabled={waEnviando || !telefone || (!waMensagem.trim() && !templateId)}
                className="w-full px-3 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-40 flex items-center justify-center gap-2">
                <Send className="h-4 w-4" />
                {waEnviando ? 'Enviando…' : 'Enviar WhatsApp'}
              </button>

              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Conversas anteriores</h3>
                {atividades.filter(a => a.tipo === 'whatsapp').length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Nenhuma mensagem ainda.</p>
                ) : (
                  <ol className="space-y-2">
                    {atividades.filter(a => a.tipo === 'whatsapp').map(a => {
                      const out = a.metadata?.direcao === 'out';
                      return (
                        <li key={a.id} className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                          out ? 'ml-auto bg-green-100 text-green-900' : 'bg-gray-100 text-gray-900'
                        }`}>
                          <p className="whitespace-pre-wrap">{a.descricao}</p>
                          <p className="text-[10px] mt-1 opacity-60">{fmtDateTime(a.data)} {a.metadata?.status === 'falha' && '· ⚠️ falhou'}</p>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardDrawer;
